import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import apiClient from "../utils/apiClient";
import { GoSearch } from 'react-icons/go';
import { FiUserPlus, FiUserCheck, FiX } from 'react-icons/fi';
import SearchContact from './SearchContact';
import { onlineUsersRoute, allusersroute } from "../utils/APIRoutes";
import { toggleFollow, checkIsFollowing, getFollowersCount, getFollowersList } from '../services/feed';
import { fetchUsersByIds } from '../services/users';

// Same placeholder image that was previously repeated inline three times.
const DEFAULT_AVATAR =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAAEACAIAAADTED8xAAADMElEQVR4nOzVwQnAIBQFQYXff81RUkQCOyDj1YOPnbXWPmeTRef+/3O/OyBjzh3CD95BfqICMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMO0TAAD//2Anhf4QtqobAAAAAElFTkSuQmCC";

export default function Contacts({ contacts, setListSet, changeChat, showCntcts }) {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); // full object — needed for _id in follow calls
  const [currentUserName, setCurrentUserName] = useState(undefined);
  const [currentUserImage, setCurrentUserImage] = useState(undefined);
  const [currentSelected, setCurrentSelected] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [displayedContacts, setDisplayedContacts] = useState([]);
  const [showImageZoom, setShowImageZoom] = useState(false);
  // ✅ NEW STATES
  const [selectedContact, setSelectedContact] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  // ---- Follow button (selectedContact, in the popup) ----
  const [isFollowingSelected, setIsFollowingSelected] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  // ---- Followers count + list (selectedContact, in the popup) ----
  const [followersCount, setFollowersCount] = useState(null);
  const [showFollowersList, setShowFollowersList] = useState(false);
  const [followersListUsers, setFollowersListUsers] = useState([]);
  const [loadingFollowersList, setLoadingFollowersList] = useState(false);

  const navigate = useNavigate();

  const contactsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contactsRef.current && !contactsRef.current.contains(event.target)) {
        showCntcts();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCntcts]);

  useEffect(() => {
    async function fetchData() {
      const data = await JSON.parse(
        localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
      );

      if (data) {
        setCurrentUser(data);
        setCurrentUserName(data.username);
        setCurrentUserImage(data.avatarImage);
      }
    }
    fetchData();
  }, []);

  // SECURITY: same fix already applied to ContactsMessaged.js —
  // GET /api/online-users requires a session on the backend now.
  // Swapped `axios` for `apiClient` so the cookie is actually sent,
  // and gated behind `currentUser` being resolved first (this used to
  // fire unconditionally on mount, before the separate fetchData
  // effect above had necessarily populated currentUser — for an
  // anonymous visitor that's a request that can only fail, and this
  // component's own render is already gated on being logged in
  // anyway, so there's nothing to fetch for until then).
  useEffect(() => {
    if (!currentUser?._id) return;

    const fetchOnlineUsers = async () => {
      try {
        const onlineUsersData = await apiClient.get(onlineUsersRoute);
        setOnlineUsers(onlineUsersData.data);
      } catch (err) {
        console.error("Error fetching online users:", err.message);
      }
    };
    fetchOnlineUsers()
  }, [currentUser]);

  useEffect(() => {
    if (contacts.length) {
      const updatedContacts = contacts.map((contact) => ({
        ...contact,
        isOnline: onlineUsers.some((user) => user.userId === contact._id),
      }));
      const shuffled = [...updatedContacts].sort(() => 0.5 - Math.random());
      setDisplayedContacts(shuffled.slice(0, 10));
    }
  }, [contacts, onlineUsers]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        if (searchQuery.trim().length > 0) {
          setSearching(true);
          setSearchLoading(true);

          const currentUserFromStorage = JSON.parse(
            localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
          );

          // Defensive only — this search box is already unreachable
          // for an anonymous visitor since the whole component's
          // render is gated on being logged in, so this doesn't
          // change any actual behavior. It just stops
          // currentUserFromStorage._id from throwing a TypeError if
          // that ever isn't true (e.g. this component gets reused
          // somewhere without that gate, or the session is cleared
          // mid-search).
          if (!currentUserFromStorage?._id) {
            setSearchResults([]);
            return;
          }

          // SECURITY: swapped `axios` for `apiClient` — this needs
          // the session cookie the same as every other authenticated
          // call in the app.
          const { data } = await apiClient.get(
            `${allusersroute}/${currentUserFromStorage._id}?search=${searchQuery}`
          );

          setSearchResults(data.users || []);
        } else {
          setSearchResults([]);
          setSearching(false);
        }
      } catch (error) {
        console.error("Error searching contacts:", error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(() => {
      fetchSearchResults();
    }, 400);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // ---------------------------------------------------------------
  // Follow status + followers count for whichever contact's popup is
  // currently open. Reset immediately on selectedContact change so a
  // stale count/follow-state from the PREVIOUS contact never
  // flashes before the fresh fetch resolves.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!selectedContact?._id) {
      setFollowersCount(null);
      setIsFollowingSelected(false);
      return;
    }

    setFollowersCount(null);
    setIsFollowingSelected(false);

    let cancelled = false;

    getFollowersCount(selectedContact._id)
      .then((count) => {
        if (!cancelled) setFollowersCount(count);
      })
      .catch((err) => console.error('Followers count fetch failed:', err));

    if (currentUser?._id && currentUser._id !== selectedContact._id) {
      checkIsFollowing(currentUser._id, selectedContact._id)
        .then((result) => {
          if (!cancelled) setIsFollowingSelected(result);
        })
        .catch((err) => console.error('Follow status check failed:', err));
    }

    return () => {
      cancelled = true;
    };
  }, [selectedContact, currentUser]);

  const changeCurrentChat = (index, contact) => {
    setCurrentSelected(index);
    changeChat(contact);
    setSearching(false);
    setListSet(true);
    showCntcts();
  };

  // ✅ NEW HANDLERS
  const handleChat = () => {
    if (selectedContact) {
      changeChat(selectedContact);
      setListSet(true);
      setShowPopup(false);
      showCntcts();
    }
  };

  const handleShop = () => {
    if (selectedContact) {
      navigate(`/shop/${selectedContact._id}`);
      setShowPopup(false);
      showCntcts();
    }
  };

  // ---------------------------------------------------------------
  // Follow / Unfollow the currently-opened popup's contact. Same
  // optimistic-update-then-reconcile pattern already used for posts
  // in UploadForm.js's PostCard.
  // ---------------------------------------------------------------
  const handleFollowToggle = async () => {
    if (!currentUser?._id || !selectedContact?._id || followBusy) return;
    if (currentUser._id === selectedContact._id) return;

    setFollowBusy(true);
    const optimistic = !isFollowingSelected;
    setIsFollowingSelected(optimistic);
    setFollowersCount((prev) => (prev === null ? prev : Math.max(0, prev + (optimistic ? 1 : -1))));

    try {
      const result = await toggleFollow(currentUser._id, selectedContact._id);
      setIsFollowingSelected(result.following);
      if (result.following !== optimistic) {
        // Server disagreed with our guess (rare) — correct the count too.
        setFollowersCount((prev) => (prev === null ? prev : Math.max(0, prev + (result.following ? 1 : -1))));
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
      setIsFollowingSelected(!optimistic);
      setFollowersCount((prev) => (prev === null ? prev : Math.max(0, prev + (optimistic ? -1 : 1))));
    } finally {
      setFollowBusy(false);
    }
  };

  // ---------------------------------------------------------------
  // Followers count indicator -> tap to see who they are. Same
  // getFollowersList + fetchUsersByIds resolution UploadForm.js's
  // UserListModal already uses for a post's likers/followers.
  // ---------------------------------------------------------------
  const handleFollowersCountClick = async () => {
    if (!selectedContact?._id) return;
    setShowFollowersList(true);
    setLoadingFollowersList(true);
    setFollowersListUsers([]);

    try {
      const ids = await getFollowersList(selectedContact._id);
      const users = await fetchUsersByIds(ids);
      setFollowersListUsers(users);
    } catch (err) {
      console.error('Failed to load followers list:', err);
    } finally {
      setLoadingFollowersList(false);
    }
  };

  const sortedContacts = [...displayedContacts].sort((a, b) => a.username.localeCompare(b.username));

  return (
    <>
      {currentUserImage && currentUserImage && (
        <Container ref={contactsRef}>
          {/* ===== HEADER: compact, never shrinks ===== */}
          <div className="current-user">
            <div className="avatar" style={{ display: 'flex', position: 'absolute', top: '0px', right: '0' }}>
              <img
                src={
                  currentUserImage?.startsWith('http')
                    ? currentUserImage
                    : `data:image/svg+xml;base64,${currentUserImage}`
                }
                alt="avatar"
                hidden
              />
              <div className="username">
                <h2 hidden>{currentUserName}</h2>
              </div>
            </div>

            <div className="search-form"
              style={{
                display: 'flex',
                width: '90%',
                marginLeft: '1%',
                backgroundColor: '#f0f0f0',
                border: 'solid',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '10px',
              }}>
              <div><GoSearch /></div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                required
                placeholder="User/Brand"
                style={{ width: '60%', height: '20px', borderRadius: '5px', border: 'none', outline: 'none' }}
              />
            </div>
          </div>

          {/* ===== LIST AREA: takes ALL remaining height ===== */}
          <div className="contacts-area">
            {searching ? (
              <div className="contacts">
                {searchLoading ? (
                  <p style={{ textAlign: "center" }}>Searching...</p>
                ) : (
                  <SearchContact
                    searchResults={searchResults}
                    sortedContacts={sortedContacts}
                    currentSelected={currentSelected}
                    changeCurrentChat={changeCurrentChat}
                    onContactClick={(index, contact) => {
                      setSelectedContact(contact);
                      setCurrentSelected(index);
                      setShowPopup(true);
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="contacts">
                {sortedContacts.length > 0 &&
                  sortedContacts.map((contact, index) => {
                    return (
                      <div
                        key={contact._id}
                        className={`contact ${index === currentSelected ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedContact(contact);
                          setCurrentSelected(index);
                          setShowPopup(true);
                        }}
                      >
                        <div className="avatar">
                          {contact.isOnline && (
                            <span className="online-dot">
                              <p style={{ fontSize: '8px', color: 'green', margin: "0" }}>online</p>
                            </span>
                          )}

                          {contact.avatarImage ? (
                            <>
                              {contact.avatarImage.startsWith("https://") || contact.avatarImage.includes("https") ? (
                                <img
                                  src={contact.avatarImage}
                                  alt={`${contact.username}'s avatar`}
                                />
                              ) : (
                                <img
                                  src={`data:image/svg+xml;base64,${contact.avatarImage}`}
                                  alt={`${contact.username}'s avatar`}
                                />
                              )}
                            </>
                          ) : (
                            <img
                              src={DEFAULT_AVATAR}
                              alt="Default Avatar"
                            />
                          )}
                          <b>
                            <p style={{ margin: "0" }}>{contact.username}</p>
                          </b>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* ✅ POPUP */}

          {showPopup && selectedContact && (
            <>
              {/* MAIN POPUP */}
              <div className="popup-overlay">
                <div className="popup">

                  {/* CLOSE */}
                  <span
                    className="close-user"
                    onClick={() => setShowPopup(false)}
                  >
                    ✕
                  </span>

                  {/* AVATAR + FOLLOW BUTTON ROW */}
                  <div className="popup-avatar-row">
                    <div className="popup-avatar">
                      <img
                        src={
                          selectedContact.avatarImage
                            ? selectedContact.avatarImage.startsWith("https")
                              ? selectedContact.avatarImage
                              : `data:image/svg+xml;base64,${selectedContact.avatarImage}`
                            : DEFAULT_AVATAR
                        }
                        alt={selectedContact.username}
                        onClick={() => setShowImageZoom(true)}
                      />
                    </div>

                    {/* FOLLOW BUTTON — same toggleFollow/checkIsFollowing
                        flow used in UploadForm.js, hidden for your own
                        contact card and when not signed in. */}
                    {currentUser?._id && currentUser._id !== selectedContact._id && (
                      <button
                        type="button"
                        className={`popup-follow-button ${isFollowingSelected ? 'following' : ''}`}
                        disabled={followBusy}
                        onClick={handleFollowToggle}
                      >
                        {isFollowingSelected ? <FiUserCheck /> : <FiUserPlus />}
                      </button>
                    )}
                  </div>

                  {/* NAME */}
                  <h3 className="popup-name">{selectedContact.username}</h3>

                  {/* FOLLOWERS COUNT — tap to see who they are */}
                  <p
                    className="popup-followers-count"
                    onClick={handleFollowersCountClick}
                  >
                    {followersCount === null ? 'Loading followers…' : `${followersCount} follower${followersCount === 1 ? '' : 's'}`}
                  </p>

                  {/* ACTIONS */}
                  <div className="popup-actions">
                    <button onClick={handleChat}>Chat</button>
                    <button onClick={handleShop}>Shop</button>
                  </div>

                </div>
              </div>

              {/* IMAGE ZOOM OVERLAY - IMPROVED QUALITY */}
              {showImageZoom && (
                <div
                  className="zoom-overlay"
                  onClick={() => setShowImageZoom(false)}
                >
                  <div className="zoom-container">
                    <img
                      src={
                        selectedContact.avatarImage
                          ? selectedContact.avatarImage.startsWith("https")
                            ? selectedContact.avatarImage
                            : `data:image/svg+xml;base64,${selectedContact.avatarImage}`
                          : DEFAULT_AVATAR
                      }
                      alt="Zoomed Avatar"
                      className="zoomed-image"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* FOLLOWERS LIST OVERLAY — same list-of-users pattern as
                  UploadForm.js's UserListModal, kept local to this file
                  since that component isn't exported for reuse. */}
              {showFollowersList && (
                <div
                  className="followers-list-overlay"
                  onClick={() => setShowFollowersList(false)}
                >
                  <div className="followers-list-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="followers-list-header">
                      <h4>{selectedContact.username}'s followers</h4>
                      <span
                        className="close-user"
                        onClick={() => setShowFollowersList(false)}
                        style={{ position: 'static' }}
                      >
                        <FiX size={18} />
                      </span>
                    </div>

                    <div className="followers-list-body">
                      {loadingFollowersList ? (
                        <p className="followers-list-status">Loading...</p>
                      ) : followersListUsers.length === 0 ? (
                        <p className="followers-list-status">No one yet.</p>
                      ) : (
                        followersListUsers.map((u) => (
                          <div key={u._id} className="followers-list-row">
                            {u.avatarImage ? (
                              <img
                                src={u.avatarImage.startsWith('http') ? u.avatarImage : `data:image/svg+xml;base64,${u.avatarImage}`}
                                alt={u.username}
                                className="followers-list-avatar"
                              />
                            ) : (
                              <div className="followers-list-avatar followers-list-avatar-placeholder">
                                {u.username?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <span className="followers-list-name">{u.username}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </Container>
      )}
    </>
  );
}

const Container = styled.div`
  /* STRICT COLUMN: [compact header] + [list that fills everything left].
     Replaces the old 10% / 75% / 15% grid and the fixed 60vh list, which
     left a big unused band at the bottom of the screen. */
  display: flex;
  flex-direction: column;
  height: 100vh;               /* fallback */
  height: 100dvh;              /* real visible height on mobile (URL-bar aware) */
  overflow: hidden;            /* only the contacts list scrolls */
  background: #eaf2ff;

  .contacts-area {
    flex: 1 1 0;
    min-height: 0;             /* lets the flex child shrink so the list can scroll */
    display: flex;
    flex-direction: column;
    background: #eaf2ff;
  }

  .contacts {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: #eaf2ff;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c6d6ff;
      border-radius: 10px;
    }

    .contact {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      min-height: 60px;
      padding: 5px 7px;
      cursor: pointer;
      background: #f4f8ff;
      border-bottom: 1px solid #dbe7ff;
      transition: background 0.2s ease;
      flex-shrink: 0;          /* rows keep their height; the list scrolls instead */

      &:hover {
        background: #e4efff;
      }

      .avatar {
        position: relative;
        flex-shrink: 0;

        img {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          object-fit: cover;
        }

        .online-dot {
          position: absolute;
          bottom: -2px;
          right: -2px;
          background: #22c55e;
          border-radius: 50%;
          width: 10px;
          height: 10px;
          border: 2px solid white;
        }
      }

      b {
        flex: 1;
        overflow: hidden;
      }

      p {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .current-user {
    flex: 0 0 auto;            /* header never shrinks */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 0 6px;        /* replaces the <br/><br/><br/> and "____" spacers */

    .avatar img {
      height: 4rem;
      max-inline-size: 100%;
    }

    .username h2 {
      color: black;
    }

    @media screen and (min-width: 720px) and (max-width: 1080px) {
      gap: 0.5rem;

      .username h2 {
        font-size: 1rem;
      }
    }
  }

  /* ✅ POPUP STYLES */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}

/* POPUP CONTAINER */
.popup {
  width: 100%;
  height: 75vh;
  background: #fff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: 20px;
  position: relative;
  animation: contactsSlideUp 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* SLIDE ANIMATION */
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* CLOSE BUTTON */
.close-user {
  position: absolute;
  top: 15px;
  right: 20px;
  font-size: 22px;
  cursor: pointer;
}

/* AVATAR + FOLLOW BUTTON ROW */
.popup-avatar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* AVATAR */
.popup-avatar img {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.popup-avatar img:hover {
  transform: scale(1.05);
}

/* FOLLOW BUTTON (to the right of the popup avatar) */
.popup-follow-button {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  background: white;
  color: #111;
  font-weight: 600;
  font-size: 13px;
}

.popup-follow-button.following {
  background: white;
  color: #111;
}

.popup-follow-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* NAME */
.popup-name {
  margin-top: 12px;
  font-size: 18px;
  font-weight: 600;
}

/* FOLLOWERS COUNT — sits right below the name */
.popup-followers-count {
  margin: 2px 0 0;
  font-size: 13px;
  color: #111;
  cursor: pointer;
  text-decoration: none;
}

/* ACTION BUTTONS */
.popup-actions {
  margin-top: 20px;
  display: flex;
  gap: 15px;
}

.popup-actions button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: #4f8cff;
  color: #fff;
  font-weight: 500;
}

/* FOLLOWERS LIST OVERLAY */
.followers-list-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
}

.followers-list-modal {
  width: 85%;
  max-width: 340px;
  max-height: 70vh;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  overflow-y: auto;
}

.followers-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    font-size: 15px;
  }
}

.followers-list-status {
  text-align: center;
  color: #888;
  font-size: 13px;
}

.followers-list-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.followers-list-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.followers-list-avatar-placeholder {
  background: #4f8cff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.followers-list-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

/* ========================= */
/* IMPROVED ZOOM OVERLAY - NO BLUR */
/* ========================= */

.zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.zoom-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 90vw;
  max-height: 90vh;
  padding: 20px;
}

/* IMPROVED ZOOMED IMAGE - MAINTAINS QUALITY */
.zoomed-image {
  max-width: 85vw;
  max-height: 85vh;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
  animation: contactsZoomIn 0.3s ease;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  /* CRITICAL: Prevents image blur during zoom */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: pixelated;

  /* Hardware acceleration for smoother rendering */
  transform: translateZ(0);
  backface-visibility: hidden;

  /* Ensures the image doesn't get interpolated poorly */
  -webkit-font-smoothing: antialiased;
}

/* Optional: Add pinch/click to zoom further */
.zoomed-image:active {
  transform: scale(1.5);
  cursor: zoom-out;
  transition: transform 0.2s ease;
}

/* ANIMATIONS */
@keyframes zoomIn {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Mobile optimization */
@media (max-width: 768px) {
  .zoomed-image {
    max-width: 90vw;
    max-height: 70vh;
  }

  .zoom-container {
    padding: 10px;
  }
}
`;